import { useCallback, useContext, useSyncExternalStore } from 'react'
import { CacheContext } from './CacheProvider'
import type { CacheSliceState } from './types'
import type { CacheSliceKey, CacheSliceData } from './slices'

/** Read a single slice — re-renders only when that slice changes */
export function useCacheSlice<K extends CacheSliceKey>(
  key: K,
): CacheSliceState<CacheSliceData<K>> {
  const ctx = useContext(CacheContext)
  if (!ctx) {
    throw new Error('useCacheSlice must be used within a CacheProvider')
  }

  const { store } = ctx

  const subscribe = useCallback(
    (callback: () => void) => store.subscribe(key, callback),
    [store, key],
  )

  const getSnapshot = useCallback(
    () => store.getSnapshot<CacheSliceData<K>>(key),
    [store, key],
  )

  return useSyncExternalStore(subscribe, getSnapshot)
}

/** Access mutation helpers */
export function useCacheMutations() {
  const ctx = useContext(CacheContext)
  if (!ctx) {
    throw new Error('useCacheMutations must be used within a CacheProvider')
  }

  return {
    refresh: ctx.refresh,
    refreshAll: ctx.refreshAll,
    optimisticUpdate: ctx.optimisticUpdate,
  }
}
